import type { ClientMessage, ServerMessage } from "../realtime/types";

export type ConnectionStatus = "connecting" | "connected" | "disconnected" | "disconnected_permanent";
type EventHandler = (data: unknown, isSnapshot: boolean) => void;
type StatusHandler = (status: ConnectionStatus) => void;

const RECONNECT_DELAYS = [500, 1000, 2000, 5000, 10000];
const HEARTBEAT_INTERVAL = 25000;
const RPC_TIMEOUT = 15000;

export class WsClient {
  readonly instanceId = crypto.randomUUID();

  private ws: WebSocket | null = null;
  private token: string | null = null;
  private msgId = 0;
  private subId = 0;
  private status: ConnectionStatus = "disconnected";
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private intentionalClose = false;
  private disconnectReason: string | null = null;
  private _limitedMode = false;
  public onForceDisconnect: (() => void) | null = null;
  public onPermissionsChanged: ((limitedMode: boolean) => void) | null = null;

  private pendingRpcs = new Map<
    number,
    { resolve: (data: unknown) => void; reject: (err: Error) => void; timer: ReturnType<typeof setTimeout> }
  >();

  private subscriptions = new Map<string, { topic: string; params?: unknown; handler: EventHandler }>();
  private statusHandlers = new Set<StatusHandler>();

  connect(token: string) {
    this.token = token;
    this.intentionalClose = false;
    this.openSocket();
  }

  disconnect() {
    this.intentionalClose = true;
    this.cleanup();
  }

  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    handler(this.status);
    return () => this.statusHandlers.delete(handler);
  }

  getStatus(): ConnectionStatus {
    return this.status;
  }

  getDisconnectReason(): string | null {
    return this.disconnectReason;
  }

  get limitedMode(): boolean {
    return this._limitedMode;
  }

  subscribe(topic: string, handler: EventHandler, params?: unknown): string {
    const id = `sub_${++this.subId}`;
    const isFirstForTopic = !this.hasSubscribersForTopic(topic);
    this.subscriptions.set(id, { topic, params, handler });
    if (this.status === "connected" && isFirstForTopic) this.sendSubscribe(topic, params);
    return id;
  }

  unsubscribe(subscriptionId: string) {
    const sub = this.subscriptions.get(subscriptionId);
    if (!sub) return;
    this.subscriptions.delete(subscriptionId);
    if (this.status === "connected" && !this.hasSubscribersForTopic(sub.topic)) {
      this.send({ type: "unsubscribe", id: this.nextId(), topic: sub.topic });
    }
  }

  resubscribe(topic: string, params?: unknown) {
    if (this.status === "connected") this.sendSubscribe(topic, params);
  }

  private hasSubscribersForTopic(topic: string): boolean {
    for (const [, sub] of this.subscriptions) {
      if (sub.topic === topic) return true;
    }
    return false;
  }

  async rpc<T = unknown>(method: string, params?: unknown): Promise<T> {
    if (this.status !== "connected") {
      throw new Error("WebSocket not connected");
    }

    const id = this.nextId();
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingRpcs.delete(id);
        reject(new Error(`RPC timeout: ${method}`));
      }, RPC_TIMEOUT);

      this.pendingRpcs.set(id, {
        resolve: resolve as (data: unknown) => void,
        reject,
        timer,
      });

      this.send({ type: "rpc", id, method, params });
    });
  }

  private nextId(): number {
    return ++this.msgId;
  }

  private setStatus(status: ConnectionStatus) {
    if (this.status === status) return;
    this.status = status;
    for (const handler of this.statusHandlers) handler(status);
  }

  private openSocket() {
    this.cleanup();
    if (!this.token) return;

    this.setStatus("connecting");

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${protocol}//${window.location.host}/ws?token=${encodeURIComponent(this.token)}&instanceId=${this.instanceId}`;

    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectAttempt = 0;
      this.setStatus("connected");
      this.startHeartbeat();
      this.resubscribeAll();
    };

    ws.onmessage = (e) => {
      const msg: ServerMessage = JSON.parse(e.data);
      this.handleMessage(msg);
    };

    ws.onclose = () => {
      this.stopHeartbeat();
      if (this.status !== "disconnected_permanent") {
        this.setStatus("disconnected");
      }
      if (!this.intentionalClose) this.scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose fires after onerror, reconnect handled there
    };
  }

  private handleMessage(msg: ServerMessage) {
    switch (msg.type) {
      case "pong":
        break;

      case "force_disconnect": {
        this.disconnectReason = msg.reason;
        this.intentionalClose = true;
        this.setStatus("disconnected_permanent");
        if (this.onForceDisconnect) {
          this.onForceDisconnect();
        }
        if (this.ws) {
          this.ws.close();
        }
        break;
      }

      case "permissions_changed": {
        this._limitedMode = msg.restrictions.limitedMode;
        if (this.onPermissionsChanged) {
          this.onPermissionsChanged(msg.restrictions.limitedMode);
        }
        if (!msg.restrictions.limitedMode) {
          this.resubscribeAll();
        }
        break;
      }

      case "subscribe_ok": {
        for (const [, sub] of this.subscriptions) {
          if (sub.topic === msg.topic) {
            sub.handler(msg.data, true);
          }
        }
        break;
      }

      case "event": {
        for (const [, sub] of this.subscriptions) {
          if (sub.topic === msg.topic) {
            sub.handler(msg.data, false);
          }
        }
        break;
      }

      case "rpc_result": {
        const pending = this.pendingRpcs.get(msg.id);
        if (pending) {
          clearTimeout(pending.timer);
          this.pendingRpcs.delete(msg.id);
          if (msg.error) {
            pending.reject(new Error(msg.error));
          } else {
            pending.resolve(msg.data);
          }
        }
        break;
      }

      case "error":
        if (msg.id) {
          const pending = this.pendingRpcs.get(msg.id);
          if (pending) {
            clearTimeout(pending.timer);
            this.pendingRpcs.delete(msg.id);
            pending.reject(new Error(msg.error));
          }
        }
        break;
    }
  }

  private sendSubscribe(topic: string, params?: unknown) {
    this.send({ type: "subscribe", id: this.nextId(), topic, params });
  }

  private resubscribeAll() {
    const seen = new Set<string>();
    for (const [, { topic, params }] of this.subscriptions) {
      const key = topic + "\0" + JSON.stringify(params);
      if (seen.has(key)) continue;
      seen.add(key);
      this.sendSubscribe(topic, params);
    }
  }

  private send(msg: ClientMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  private startHeartbeat() {
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: "ping" });
    }, HEARTBEAT_INTERVAL);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect() {
    const delay = RECONNECT_DELAYS[Math.min(this.reconnectAttempt, RECONNECT_DELAYS.length - 1)];
    this.reconnectAttempt++;
    this.reconnectTimer = setTimeout(() => this.openSocket(), delay);
  }

  private cleanup() {
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = null;
    }
    for (const [, pending] of this.pendingRpcs) {
      clearTimeout(pending.timer);
      pending.reject(new Error("Connection closed"));
    }
    this.pendingRpcs.clear();
  }
}

export const wsClient = new WsClient();
